import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Button, Input, Label, Spinner, Modal, Textarea } from "@/components/UI";
import { Camera, Volume2, Send } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    job_title: "",
    password: "",
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [feeling, setFeeling] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      email: user.email || "",
      job_title: user.job_title || "",
      password: "",
    });
    setAvatarPreview(user.avatar_url || "");
    setAvatarFile(null);
    setGreeting(user.greeting_template || "Selamat bekerja, {nama}");
    setFeeling(user.feeling || "");
  }, [user]);

  if (!user) return <Spinner />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const previewGreeting = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const text = (greeting || "Selamat bekerja, {nama}").replace(/{nama}/g, user?.name || "User");
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "id-ID";
    u.rate = 0.95;
    u.pitch = 1.05;
    window.speechSynthesis.speak(u);
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { feeling: feeling.trim() });
      setUser(data);
      setFeeling(data.feeling || "");
      setNoteOpen(false);
      toast.success("Catatan disimpan");
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menyimpan catatan");
    } finally {
      setSaving(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 2MB");
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      let data;

      if (avatarFile) {
        // PHP/Laravel tidak bisa mem-parsing file upload pada request PUT asli,
        // jadi gunakan method-spoofing: POST + field _method=PUT.
        const payload = new FormData();
        payload.append("_method", "PUT");
        payload.append("avatar", avatarFile);
        payload.append("name", form.name);
        payload.append("email", form.email);
        payload.append("job_title", form.job_title);
        payload.append("greeting_template", greeting.trim() || "Selamat bekerja, {nama}");
        if (form.password.trim()) {
          payload.append("password", form.password);
        }
        ({ data } = await api.post("/auth/me", payload));
      } else {
        const payload = {
          name: form.name,
          email: form.email,
          job_title: form.job_title,
          greeting_template: greeting.trim() || "Selamat bekerja, {nama}",
        };
        if (form.password.trim()) {
          payload.password = form.password;
        }
        ({ data } = await api.put("/auth/me", payload));
      }

      setUser(data);
      setGreeting(data.greeting_template || "Selamat bekerja, {nama}");
      setAvatarFile(null);
      setForm((f) => ({ ...f, password: "" }));
      toast.success("Profil diperbarui");
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal memperbarui profil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Profil Saya</h1>
        <p className="text-sm text-slate-400">Kelola foto, nama, dan informasi akun Anda.</p>
      </div>

      <Card className="space-y-6 p-6" data-testid="profile-card">
        <div className="flex flex-wrap items-start gap-4">
          <div className="relative shrink-0">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt={form.name}
                className="h-28 w-28 rounded-2xl object-cover ring-2 ring-slate-800"
              />
            ) : (
              <div className="flex h-28 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E293B] to-[#64748B] text-3xl font-semibold text-white ring-2 ring-[#64748B]/25">
                {(form.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full bg-slate-600 text-white shadow-md shadow-slate-500/30 hover:bg-slate-500"
              title="Ganti foto profil"
              data-testid="profile-avatar-camera"
            >
              <Camera size={16} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid="profile-avatar-input"
            />
          </div>

          <div className="flex flex-1 flex-col items-start gap-3">
            <div>
              <p className="font-heading text-2xl font-bold text-slate-100">{user.name}</p>
              <p className="text-sm text-slate-400">{user.role === "admin" ? "Admin / PM" : "Employee"}</p>
            </div>

            <div
              onClick={() => setNoteOpen(true)}
              className="group relative max-w-[260px] cursor-pointer rounded-2xl bg-slate-700 px-4 py-3 text-sm text-slate-100 shadow-md transition-colors hover:bg-slate-600"
              data-testid="profile-feeling-bubble"
              title="Ketuk untuk mengatur catatan"
            >
              <p className="line-clamp-3">{user.feeling?.trim() || "Ketuk untuk menulis..."}</p>
              <span className="absolute -left-1.5 top-5 h-3 w-3 -translate-y-1/2 rotate-45 bg-slate-700 group-hover:bg-slate-600" />
            </div>
          </div>
        </div>

        <div className="space-y-4">

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nama</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="profile-email" />
            </div>
          </div>

          <div>
            <Label>Job Title</Label>
            <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="profile-job-title" />
          </div>

          <div>
            <Label>Password Baru (kosongkan jika tidak diubah)</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              data-testid="profile-password"
            />
          </div>

          <div>
            <Label>Opening Greeting</Label>
            <p className="mb-1.5 text-xs text-slate-500">Ucapan suara saat masuk Dashboard. Gunakan <code>{"{nama}"}</code> untuk nama Anda.</p>
            <div className="flex items-center gap-2">
              <Input
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder="Selamat bekerja, {nama}"
                data-testid="greeting-input"
              />
              <Button type="button" variant="ghost" onClick={previewGreeting} data-testid="greeting-preview">
                <Volume2 size={16} />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </Button>
        </div>
      </Card>

      <Modal open={noteOpen} onClose={() => setNoteOpen(false)} title="Catatan Anda" size="sm" data-testid="feeling-modal">
        <div className="space-y-4">
          <Textarea
            rows={2}
            value={feeling}
            onChange={(e) => setFeeling(e.target.value)}
            placeholder="Apa yang sedang Anda rasakan?"
            maxLength={120}
            data-testid="feeling-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setNoteOpen(false)}>Batal</Button>
            <Button onClick={saveNote} disabled={saving || feeling.trim().length > 120} data-testid="feeling-save">
              <Send size={14} /> {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
